import { useCallback, useMemo } from 'react';
import { convertUserToParticipant } from '../../misc/convertUserToParticipant';
import { useDirectoryItemContacts } from '../directory/directoryHooks';
import { useEventQuestion } from '../questions/useEventQuestion';
import { useMyAccountAsParticipant } from '../sessionHooks';
import { useEventParticipants } from './useEventParticipants';
import { useProgramSession } from '../schedule/useProgramSession';

/**
 * Returns the event participants
 * Can look into other sources like askers/answerer or assignees
 * @param {Number}  eventId             Event ID
 * @param {array}   participantsQueries Objects with {userId, participantId} to match
 * @param {object}  filter              Filter function
 * @param {Number}  questionId          Question to include asker+answeres in the search
 * @param {Number}  directoryItemId     Directory item to include 'contacts' in the search
 * @param {Number}  sessionId           Session to include assignees in the search
 * @returns array
 */
export function useEventParticipantsList(eventId, participantsQueries, { filter: additionalFilter, questionId, directoryItemId, sessionId } = {}) {
    const participants = useEventParticipants(eventId),
        myself = useMyAccountAsParticipant(eventId),
        question = useEventQuestion(eventId, questionId),
        directoryItemContacts = useDirectoryItemContacts(eventId, directoryItemId).map((c) => ({ ...c, can_poke: false })), // can_poke:false to prevent "Invite me to networkapp button since user is most likelly hidden"
        assignees =
            useProgramSession(eventId, sessionId)?.assignees?.map((a) => ({
                ...a.participant,
                participant_id: a.participant_id,
            })) ?? [],
        queryFilter = useCallback((p) => participantsQueries.find((q) => (q.userId && q.userId == p.user_id) || (q.participantId && q.participantId == p.participant_id)), [participantsQueries]);

    return useMemo(() => {
        let result = [
            // Participants list
            ...participants,
            // User account - used when user is hidden
            myself,
            // Directory item contacts
            ...directoryItemContacts,
            // Question asker + answerers
            question?.asker,
            ...(question?.answers.data.map((answer) => convertUserToParticipant(answer.answerer)) || []),
            // Assignees
            ...assignees,
        ].filter((i) => i);

        result = result.filter(queryFilter);
        if (additionalFilter) result = result.filter(additionalFilter);

        return result;
    }, [participants, myself, queryFilter, question, directoryItemContacts, additionalFilter]);
}
